import type { ReactNode } from 'react';
import { cn } from '../cn';

// Canonical page header for every game / info surface.
//
// One source of truth for the page title, subtitle, and the gap to the content
// below. Before this existed, pages hand-rolled `<h1 className="text-[28px]
// ...">` with four different margin prefixes (none / mb-1 / mb-2 / mb-6) and
// some skipped the semantic `<header>` wrapper entirely — the "made by
// committee" drift. PageHeader removes the decision: titles are identical and
// spacing is owned here.
//
// Structure:
//   <header class="mb-8">
//     <div> h1 (+ optional right-aligned actions)
//     <p>   description — prose subtitle (optional)
//     <div> children — tabs / filters / dense readout strips (optional)
//
// Spacing contract: PageHeader owns the bottom gap (`mb-8`). Pages place it as
// the first element of their layout container; the `mb-8` collapses cleanly
// against a `space-y-*` wrapper, so the title-to-content gap is uniform.
//
// Stat-strip pages (gym, etc.) keep their dense uppercase readout by passing a
// fully-styled <span> as `description`; the inner span styling overrides the
// canonical prose wrapper, so no page regresses.

interface PageHeaderProps {
  title: string;
  /** Prose subtitle. Strings render in the canonical body style; pass a styled
   *  node to override (e.g. a dense uppercase readout strip). */
  description?: ReactNode;
  /** Right-aligned controls in the title row (e.g. a primary action button). */
  actions?: ReactNode;
  /** Rendered below the title block: tab bars, filters, readout strips. */
  children?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  actions,
  children,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('mb-8', className)}>
      <div className={cn('flex items-start gap-4', actions && 'justify-between')}>
        <h1 className="text-[28px] font-semibold leading-[1.15] tracking-[-0.01em] text-ink-high">
          {title}
        </h1>
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
      {description !== undefined && description !== null && description !== false && (
        <p className="mt-1 text-[15px] leading-[1.55] text-ink-mid">{description}</p>
      )}
      {children !== undefined && children !== null && children !== false && (
        <div className="mt-4">{children}</div>
      )}
    </header>
  );
}
